import type { HTMLAttributes, ReactNode } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FloatingPanel } from "@/components/ui/floating-panel";
import { cn } from "@/lib/utils";

interface ViewportSelectionPanelProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  headerActions?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  children: ReactNode;
}

export function ViewportSelectionPanel({
  title,
  subtitle,
  icon,
  headerActions,
  actions,
  onClose,
  closeLabel = "Close selection editor",
  onKeyDown,
  className,
  children,
  onMouseDown,
  onClick,
  ...props
}: ViewportSelectionPanelProps) {
  return (
    <FloatingPanel
      className={cn(
        "pointer-events-auto absolute right-4 top-16 z-50 w-[260px] p-2.5",
        className
      )}
      onMouseDown={(event) => {
        event.stopPropagation();
        onMouseDown?.(event);
      }}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      onKeyDown={onKeyDown}
      {...props}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon ? (
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-muted/40">
              {icon}
            </div>
          ) : null}
          <div className="min-w-0">
            <h3 className="truncate text-xs font-bold uppercase tracking-wider">
              {title}
            </h3>
            {subtitle ? (
              <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {headerActions}
          {onClose ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label={closeLabel}
            >
              <X className="size-4" />
            </Button>
          ) : null}
        </div>
      </div>
      <div className="space-y-2">{children}</div>
      {actions ? <div className="mt-2 flex justify-end gap-1">{actions}</div> : null}
    </FloatingPanel>
  );
}
